import { useCallback, useState } from 'react';

import { useAppDispatch } from '@store/store.tsx';
import { cleanCartAsync } from '@store/slices/cartSlice.tsx';

import type { CleanCartResult } from '@eshop/types/cart.types.ts';
import { notifyCartClearedError, notifyCartClearedSuccess } from '@eshop/utils/cartNotifications.ts';

interface ClearCartOutcome {
    success: boolean;
    result?: CleanCartResult;
    error?: unknown;
}

export const useClearCart = () => {
    const dispatch = useAppDispatch();
    const [isClearing, setIsClearing] = useState(false);

    const clearCart = useCallback(async (): Promise<ClearCartOutcome> => {
        setIsClearing(true);
        try {
            const action = await dispatch(cleanCartAsync());
            if (cleanCartAsync.fulfilled.match(action)) {
                notifyCartClearedSuccess(action.payload);
                return { success: true, result: action.payload };
            }

            const errorPayload = action.payload ?? action.error;
            notifyCartClearedError(errorPayload);
            return { success: false, error: errorPayload };
        } catch (error) {
            notifyCartClearedError(error);
            return { success: false, error };
        } finally {
            setIsClearing(false);
        }
    }, [dispatch]);

    return { clearCart, isClearing };
};

export type { ClearCartOutcome };
